import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreIcon } from "@/shared/assets/icons";
import { Button, Dropdown, DropdownItem } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import type { WorkerListItem } from "../../../types";

interface Props {
  employee: WorkerListItem;
  onDownloadProfile: (id: number) => void;
  isDownloading: boolean;
  onOpenAssignRole: (employee: WorkerListItem) => void;
  onRehire: (employee: WorkerListItem) => void;
  isRehiring: boolean;
  direction?: "top" | "bottom";
}

export default function EmployeeActionsDropdown({
  employee,
  onDownloadProfile,
  isDownloading,
  onOpenAssignRole,
  onRehire,
  isRehiring,
  direction = "bottom",
}: Props) {
  const { t } = useTranslation("EmployeesPage");
  const { hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const isResigned = employee.status === "resigned";
  const hasActions = isResigned ? hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE) : true;

  if (!hasActions) return null;

  return (
    <Dropdown open={isOpen} onClose={() => setIsOpen(false)} width="w-max" direction={direction} align="right">
      <Button
        variant="secondary"
        isIconButton
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-8! radius-xs! p-0!">
        <MoreIcon />
      </Button>
      {isResigned ? (
        <DropdownItem
          onClick={() => {
            onRehire(employee);
            setIsOpen(false);
          }}
          disabled={isRehiring}>
          {isRehiring ? t("rehire.loading") : t("rehire.button")}
        </DropdownItem>
      ) : (
        <>
          <DropdownItem
            onClick={() => {
              if (!isDownloading) {
                onDownloadProfile(employee.id);
                setIsOpen(false);
              }
            }}
            className={isDownloading ? "opacity-50 cursor-not-allowed" : ""}>
            {t("cards.downloadProfile")}
          </DropdownItem>
          {hasPermission(PERMISSIONS.ADMIN.MANAGE_ROLES) && employee.user_id && (
            <DropdownItem
              onClick={() => {
                onOpenAssignRole(employee);
                setIsOpen(false);
              }}>
              {t("roleAssignment.assignRole")}
            </DropdownItem>
          )}
        </>
      )}
    </Dropdown>
  );
}
